import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Row, Col, Form } from 'antd';
import { BaseStore } from 'src/store';
import { FormFiltersAccordion } from 'src/modules/form';
import { ActionCreatorType } from 'src/store';
import { RetailerCategory } from '../../store/types';
import { setFilter } from '../../store/actions';
import { getVisibleRetailers } from '../../store/selectors';
import { LayoutPage } from 'src/modules/layout';
import { RetailerEntity, OnStoreHover, ActiveRetailer } from 'src/modules/retailer';
import { Media } from 'react-breakpoints';
import { isSmallScreen } from 'src/utils/breakpoints';
import { useIntl } from 'react-intl';
import { LoadingOutlined } from '@ant-design/icons';
import { useLocale } from 'src/modules/locale';

import RetailerList from '../list/RetailerList';
import RetailerSearch from '../search/RetailerSearch';
import RetailerMap from '../map/RetailerMap';
import styles from './RetailerPage.module.scss';
import { defaultHorizontalGutter } from 'src/utils/grid';

interface RetailerPageProps {
  retailers: RetailerEntity[];
  setFilter: ActionCreatorType;
}

const RetailerPage: React.FunctionComponent<RetailerPageProps> = (props) => {
  const intl = useIntl();
  const { outputChannel } = useLocale();
  const [form] = Form.useForm();
  const [mounted, setMounted] = useState(false);
  const [activeRetailer, setActiveStore] = useState<ActiveRetailer>({ id: 0, scroll: false });
  const _onRetailerHover: OnStoreHover = (id: number, scroll = false) => {
    setActiveStore({ id, scroll });
  };
  const [isScrollingDown, setScrollingDown] = useState<boolean>(true);

  const breadcrumbs = [
    {
      title: intl.formatMessage({ id: 'breadcrumbs/store' }),
      as: '/store',
      href: '/store',
    },
  ];

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    let scrollTop = window.scrollY || document.body.scrollTop || document.documentElement.scrollTop;

    const onScroll = () => {
      const previousScrollTop = scrollTop;
      scrollTop = Math.max(
        0,
        window.scrollY || document.body.scrollTop || document.documentElement.scrollTop
      );
      if (previousScrollTop > scrollTop) {
        if (isScrollingDown) {
          setScrollingDown(false);
        }
      } else if (previousScrollTop < scrollTop) {
        if (!isScrollingDown) {
          setScrollingDown(true);
        }
      }
    };

    onScroll();

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isScrollingDown]);

  const onFiltersChanged = (filters) => {
    props.setFilter(filters.categories);
  };

  const topStoresColumnJSX = (
    <div
      className={styles.topStoresColumn}
      key={mounted ? 'topStoresColumnJSX_remount' : 'topStoresColumnJSX'}>
      <h2 className={styles.title}>{intl.formatMessage({ id: 'page/store/title' })}</h2>
      <RetailerSearch />
      <div className={styles.filters}>
        <FormFiltersAccordion
          className={styles.formFilters}
          // set resultsCount to -1 to ignore showing a label
          resultsCount={props.retailers.length || -1}
          resultsCountKey="filters/filter_labels/results"
          onChange={onFiltersChanged}
          form={form}
          filters={{
            categories: {
              labelKey: 'filters/filter_labels/categories',
              items: [
                {
                  name: intl.formatMessage({ id: 'page/store/living_spaces' }),
                  id: RetailerCategory.Living,
                  value: 'living',
                },
                {
                  name: intl.formatMessage({ id: 'page/store/office_spaces' }),
                  id: RetailerCategory.Office,
                  value: 'office',
                },
              ],
            },
          }}
        />
      </div>
    </div>
  );

  return (
    <LayoutPage
      title={intl.formatMessage({ id: 'page/store/head/page_title' })}
      description={intl.formatMessage({
        id: 'page/store/head/meta_description',
      })}
      breadcrumbs={breadcrumbs}>
      <Media>
        {({ currentBreakpoint } = { currentBreakpoint: 'lg' }) => {
          return (
            <div className={styles.storeContainer}>
              <div>
                {isSmallScreen(currentBreakpoint) && topStoresColumnJSX}
                <Row gutter={[defaultHorizontalGutter, 20]} className={styles.mainRow}>
                  <Col xs={{ span: 24, order: 2 }} md={{ span: 8, order: 1 }}>
                    <div className={styles.storesColumn}>
                      {!isSmallScreen(currentBreakpoint) && topStoresColumnJSX}
                      {props.retailers.length < 1 ? (
                        <div className={styles.loadingContainer}>
                          <LoadingOutlined />
                        </div>
                      ) : (
                        <RetailerList
                          activeRetailer={activeRetailer}
                          onRetailerHover={_onRetailerHover}
                        />
                      )}
                    </div>
                  </Col>
                  <Col xs={{ span: 24, order: 1 }} md={{ span: 16, order: 2 }}>
                    <div
                      className={classnames(
                        styles.mapContainer,
                        isScrollingDown ? styles.scrollingDown : styles.scrollingUp
                      )}>
                      <RetailerMap
                        activeRetailer={activeRetailer}
                        onRetailerHover={_onRetailerHover}
                        outputChannel={outputChannel}
                      />
                    </div>
                  </Col>
                </Row>

                {/* <LayoutBlockSeo titleKey="page/store/seo_title" sublineKey="page/store/seo_text" /> */}
              </div>
            </div>
          );
        }}
      </Media>
    </LayoutPage>
  );
};
const mapStateToProps = (state: BaseStore) => {
  return getVisibleRetailers(state);
};

export default connect(mapStateToProps, { setFilter })(RetailerPage);
